import React from 'react'
import styled from '@emotion/styled'
import theme from '@nextretreat/ui-components/dist/Theme'
import { math, rem } from 'polished'
import { Box } from 'components/atoms/Layout'
import { Label } from 'components/atoms/Typography'

export const NUMBER_SIZE = rem('32px')
export const OFFSET = rem(16)

const StyledLabel = styled(Label)`
  padding-bottom: ${rem(8)};

  line-height: 1.5;

  left: ${OFFSET};

  display: block;
`

const Content = styled('div')`
  align-self: center;
  width: 100%;
`

interface IItemProps {
  $active?: boolean
}

const Item = styled('div', {
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<IItemProps>`
  counter-increment: steps;
  flex: 1;

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  padding-bottom: ${rem(24)};
  min-height: ${NUMBER_SIZE};

  font-size: ${theme.fontSizes.m};

  &::before,
  &::after {
    left: calc(50% - calc(${NUMBER_SIZE} / 2));
  }

  &::before {
    content: counter(steps) '.';

    z-index: 1;
    position: relative;

    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;

    margin-top: -${math(`${NUMBER_SIZE} + 1rem`)};
    margin-bottom: ${OFFSET};
    padding-left: 0.15em;
    box-sizing: border-box;
    width: ${NUMBER_SIZE};
    height: ${NUMBER_SIZE};

    font-size: ${theme.fontSizes.s};

    background-color: ${theme.COLORS.BG_SOFT};
    color: ${theme.COLORS.BRAND_DEFAULT};
    box-shadow: 0 0 0 ${rem('5px')} ${theme.COLORS.WHITE};
    border-radius: ${NUMBER_SIZE};
  }

  &::after {
    content: '';

    position: absolute;
    right: -50%;
    left: 50%;
    top: -${math(`${math(`${NUMBER_SIZE} / 2`)} + 1rem`)};
    display: block;

    border-top: 1px dashed ${theme.COLORS.BG_DIVIDER};
  }

  &:last-of-type {
    padding-bottom: 0;

    &::before {
      background-color: ${theme.COLORS.WHITE};
      color: ${theme.COLORS.BRAND_DEFAULT};
      border: 1.5px solid;
    }

    &::after {
      content: unset;
    }
  }

  ${({ $active }) =>
    $active !== undefined
      ? $active
        ? `&::before {
          background-color: ${theme.COLORS.WHITE};
          color: ${theme.COLORS.BRAND_DEFAULT};
          border: 1.5px solid;
        }
        
        color: ${theme.COLORS.BRAND_DEFAULT};`
        : `&::before {
          background-color: ${theme.COLORS.BG_SOFT} !important;
          color: ${theme.COLORS.BRAND_DEFAULT} !important;
          border: 0 !important;
        }`
      : ''}
`

interface IStepProps {
  children: React.ReactNode
  className?: string
  label?: string
  active?: boolean
}

const Step = React.forwardRef<HTMLDivElement, IStepProps>(
  ({ children, className, label, active }, ref) => (
    <Item $active={active} className={className} ref={ref}>
      <Box width="100%">
        <StyledLabel>{label}</StyledLabel>
        <Content>{children}</Content>
      </Box>
    </Item>
  )
)
export default Step
